"use client";

import { useState, useEffect } from "react";
import { Plus, Users, FileText, Search, Filter } from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import {
  TableProvider,
  DataTableHeader,
  DataTableHeaderGroup,
  DataTableHead,
  DataTableBody,
  DataTableRow,
  DataTableCell,
} from "@/components/ui/data-table";
import { SlCalender } from "react-icons/sl";
import { useNavigate } from "react-router-dom";
import { get } from "../../utils/service";
import { Business } from "@/types";
import { DashboardSummary } from "./stats/DashboardSummary";
import { StatsOverview } from "./stats/StatsOverview";
import RevenueChart from "./RevenueChart";

interface DashboardProps {
  activeSection: string;
  setActiveSection: (section: string) => void;
  selectedCompany: string;
  setSelectedCompany: (companyId: string) => void;
  businesses: Business[];
  setBusinesses: React.Dispatch<React.SetStateAction<Business[]>>;
  loading: boolean;
  onRefresh: () => void;
}

interface EnhancedDashboardContentProps {
  loading: boolean;
  onRefresh: () => void;
}

interface DashboardData {
  stats: {
    total_leads: number;
    total_projects: number;
    active_users: number;
    total_revenue: number;
    currency?: string;
  };
  pipeline: Array<{
    stage: number;
    stage_name: string;
    leads_count: number;
    projects_count: number;
    total_count: number;
    total_value: number;
    currency?: string;
  }>;
  sales_by_month: Array<{
    month: string;
    sales: number;
    leads: number;
    conversions: number;
    currency?: string;
  }>;
  conversion_rate: {
    overall_rate: number;
    total_leads: number;
    converted: number;
  };
  revenue_by_type: Array<{
    type: string;
    revenue: number;
    count: number;
    currency?: string;
  }>;
  recent_activity: Array<unknown>;
  top_deals: Array<{
    id: string;
    name: string;
    client: string;
    value: number;
    stage: number;
    status: string;
    type: string;
    progress: number;
    currency?: string;
  }>;
  quick_stats: {
    total_pipeline_value: number;
    active_leads: number;
    active_projects: number;
    won_this_month: number;
    currency?: string;
  };
}

interface DashboardResponse {
  success: boolean;
  message: string;
  data: DashboardData;
  timestamp: string;
}

const PROJECT_TYPES = [
  "app_development",
  "web_development",
  "mobile_app",
  "web_design",
  "ui_ux_design",
  "figma_design",
  "talent_placement",
  "software_consulting",
  "api_development",
  "database_design",
  "cloud_infrastructure",
  "devops",
  "qa_testing",
  "maintenance_support",
  "custom_software"
];

const SkeletonTable = () => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
    <div className="animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-center gap-4">
            <div className="h-10 bg-gray-200 rounded flex-1"></div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const getCurrency = (data: any, fallbackCurrency: string = "USD"): string => {
  if (data?.currency) return data.currency;
  return fallbackCurrency;
};

const QuickActions = () => {
  const navigate = useNavigate();

  const actions = [
    {
      icon: Plus,
      title: "Add New Lead",
      description: "Capture new business opportunity",
      color: "bg-blue-500",
      path: "/sales/leads",
    },
    {
      icon: Users,
      title: "Manage Team",
      description: "View and assign team members",
      color: "bg-green-500",
      path: "/users",
    },
    {
      icon: FileText,
      title: "View Projects",
      description: "Monitor project portfolio",
      color: "bg-purple-500",
      path: "/projects/all",
    },
    {
      icon: SlCalender,
      title: "Calendar",
      description: "Manage meetings and events",
      color: "bg-orange-500",
      path: "/calendar",
    },
  ];

  const handleActionClick = (action: (typeof actions)[0]) => {
    navigate(action.path);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs text-gray-500 font-medium mb-1">
            Quick Actions
          </p>
          <h3 className="text-xs font-bold text-gray-900">
            Frequently Used Tasks
          </h3>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {actions.map((action, index) => (
          <button
            key={index}
            onClick={() => handleActionClick(action)}
            className="border border-gray-200 rounded-xl p-4 hover:border-primary hover:shadow-md transition-all duration-200 text-left group"
          >
            <div className="flex items-center gap-3 mb-2">
              <div
                className={`p-2 rounded-lg ${action.color} text-white group-hover:scale-110 transition-transform`}
              >
                <action.icon className="w-3 h-3" />
              </div>
              <h4 className="text-xs font-bold text-gray-900 group-hover:text-primary">
                {action.title}
              </h4>
            </div>
            <p className="text-xs text-gray-600 group-hover:text-gray-800">
              {action.description}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
};

const ProjectLeadsTable = ({
  dashboardData,
  loading,
}: {
  dashboardData: DashboardData | null;
  loading: boolean;
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  if (loading || !dashboardData) {
    return <SkeletonTable />;
  }

  const formatCurrency = (amount: number, currency: string = "USD") => {
    if (amount >= 1000000000) {
      return `${currency} ${(amount / 1000000000).toFixed(1)}B`;
    } else if (amount >= 1000000) {
      return `${currency} ${(amount / 1000000).toFixed(0)}M`;
    }
    return `${currency} ${amount.toLocaleString()}`;
  };

  const leads = dashboardData.top_deals.filter(deal => {
    const matchesSearch = 
      deal.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      deal.client.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = typeFilter === "all" || deal.type === typeFilter;
    
    return matchesSearch && matchesType;
  });

  const mainCurrency =
    getCurrency(dashboardData.stats) ||
    getCurrency(dashboardData.quick_stats) ||
    "USD";

  const handleRowClick = () => {
    navigate(`/projects/all`);
  };

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="font-medium text-xs text-gray-900">
          {row.original.name}
        </div>
      ),
    },
    {
      accessorKey: "client",
      header: "Client",
      cell: ({ row }) => (
        <div className="text-xs text-gray-700">
          {row.original.client}
        </div>
      ),
    },
    {
      accessorKey: "value",
      header: "Value",
      cell: ({ row }) => {
        const currency = getCurrency(row.original, mainCurrency);
        return (
          <span className="text-xs font-semibold text-gray-900">
            {formatCurrency(row.original.value, currency)}
          </span>
        );
      },
    },
    // {
    //   accessorKey: "type",
    //   header: "Type",
    //   cell: ({ row }) => (
    //     <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
    //       {row.original.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
    //     </span>
    //   ),
    // },
    // {
    //   accessorKey: "currency",
    //   header: "Currency",
    //   cell: ({ row }) => (
    //     <div className="text-xs text-gray-700 font-medium">
    //       {row.original.currency || mainCurrency}
    //     </div>
    //   ),
    // },
  ];

  return (
    <div className="bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-xs font-semibold text-gray-900">
              Recent Transactions
            </h2>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            
            <div className="relative">
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="flex items-center justify-center gap-2 px-4 py-2 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors w-full sm:w-auto"
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
              </button>
              
              {isFilterOpen && (
                <div className="absolute top-full right-0 mt-2 w-48 bg-black border border-gray-700 rounded-lg shadow-lg z-10">
                  <div className="p-3">
                    <label className="text-xs font-semibold text-white mb-2 block">Type</label>
                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      className="w-full px-3 py-2 text-xs bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
                    >
                      <option value="all">All Types</option>
                      {PROJECT_TYPES.map(type => (
                        <option key={type} value={type}>
                          {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <TableProvider columns={columns} data={leads}>
          <DataTableHeader>
            {({ headerGroup }) => (
              <DataTableHeaderGroup headerGroup={headerGroup}>
                {({ header }) => <DataTableHead header={header} />}
              </DataTableHeaderGroup>
            )}
          </DataTableHeader>
          <DataTableBody>
            {({ row }) => (
              <DataTableRow
                row={row}
                className="hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => handleRowClick(row.original)}
              >
                {({ cell }) => <DataTableCell cell={cell} />}
              </DataTableRow>
            )}
          </DataTableBody>
        </TableProvider>

        {leads.length === 0 && (
          <div className="text-center py-8">
            <p className="text-xs text-gray-600">No transactions found</p>
            {searchQuery || typeFilter !== "all" && (
              <p className="text-xs text-gray-500 mt-1">Try adjusting your search or filters</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function EnhancedDashboardContent(_props: EnhancedDashboardContentProps) {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await get<DashboardResponse>("/dashboard");
      if (response.success) {
        setDashboardData(response.data);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="max-w-7xl mx-auto">
        <div className="space-y-6">
          <DashboardSummary dashboardData={dashboardData} loading={loading} />

          <StatsOverview dashboardData={dashboardData} loading={loading} />

          <QuickActions />

          <RevenueChart />

          <ProjectLeadsTable dashboardData={dashboardData} loading={loading} />
        </div>
      </div>
    </div>
  );
}

export function Dashboard({ loading, onRefresh }: DashboardProps) {
  return (
    <div className="w-full h-full">
      <EnhancedDashboardContent loading={loading} onRefresh={onRefresh} />
    </div>
  );
}